import { MouseEvent, ReactElement, ReactNode } from 'react';
import { Tooltip } from 'react-tooltip';

export const sizeMap = {
  small: 8,
  medium: 10,
  large: 12, // adding .5 for padding on icon
  xl: 14, // adding 1 for padding on icon
};

const typeMap = {
  primary: 'bg-primary-400 hover:bg-primary-300 active:enabled:shadow-focused',
  secondary: 'bg-grey-200 hover:bg-grey-100 active:enabled:shadow-focused-dark',
  subtle: 'hover:bg-primary-t-1000 active:enabled:shadow-focused-dark',
  delete: 'hover:bg-red-t-1000 active:enabled:shadow-focused-dark',
  tertiary:
    'border border-grey-t-600 border-inset hover:bg-grey-t-800 active:enabled:shadow-focused-dark disabled:border-none ',
  basic:
    'hover:bg-grey-t-900 active:enabled:shadow-focused-dark [&[data-active]:not([data-active="false"])]:bg-grey-t-900',
  light: 'bg-grey-t-900 hover:bg-grey-t-800 active:enabled:shadow-focused-dark',
};

const typeMapDisabled = {
  primary: 'disabled:bg-background-disabled',
  secondary: 'disabled:bg-background-disabled',
  subtle: '',
  delete: '',
  tertiary: '',
  basic: '',
  light: 'disabled:bg-background-disabled',
};

const strokeTypeMap = {
  primary: 'stroke-text-filled-component',
  secondary: 'stroke-text-filled-component',
  subtle: 'stroke-primary-400',
  delete: 'stroke-red-400',
  tertiary: 'stroke-white',
  basic: 'stroke-white',
  light: 'stroke-white',
};

export const iconSizeMap = {
  small: 'size-4',
  medium: 'size-4',
  large: 'size-5',
  xl: 'size-6',
};

export type IconButtonSizeMap = keyof typeof sizeMap;

export type IconButtonTypeMap = keyof typeof typeMap;

export interface IconButtonProps {
  children?: ReactNode | undefined;
  Icon: HeroIconType;
  onClick?: ((e: MouseEvent<HTMLButtonElement>) => void) | undefined;
  size?: IconButtonSizeMap | undefined;
  type?: IconButtonTypeMap | undefined;
  disabled?: boolean | undefined;
  tooltip?: string | undefined;
  tooltipId?: string | undefined;
  active?: boolean | undefined;
  iconClassName?: string | undefined;
  className?: string | undefined;
  isSubmit?: boolean | undefined;
  id?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-expanded'?: boolean | undefined;
  'aria-controls'?: string | undefined;
}

export type IconButtonClassnameProps = Pick<IconButtonProps, 'disabled'> & {
  type: NonNullable<IconButtonProps['type']>;
  size: NonNullable<IconButtonProps['size']>;
};

export const getIconButtonStyles = ({
  type,
  size,
  disabled,
  additionalClassnames,
}: IconButtonClassnameProps & {
  additionalClassnames?: string | undefined;
}) => {
  const className = `disabled:text-disabled-text relative rounded-xs group outline-none ${
    !disabled ? typeMap[type] : typeMapDisabled[type]
  } ${additionalClassnames}`;

  const sizeInPX = sizeMap[size];

  return {
    className,
    style: {
      padding: `${sizeInPX - (className.includes('border') ? 1 : 0)}px`,
    },
  };
};

export const IconButtonIcon = ({
  Icon,
  size,
  type,
  iconClassName,
}: IconButtonClassnameProps & {
  Icon: NonNullable<IconButtonProps['Icon']>;
  iconClassName: NonNullable<IconButtonProps['iconClassName']>;
}) => (
  <div className="p-0.5">
    <Icon
      className={`${iconSizeMap[size]} ${strokeTypeMap[type]} group-disabled:stroke-text-disabled ${iconClassName}`}
    />
  </div>
);

export const IconButton = ({
  Icon,
  children,
  onClick,
  size = 'medium',
  type = 'basic',
  disabled,
  tooltip,
  tooltipId,
  active,
  iconClassName,
  className,
  isSubmit,
  id,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
}: IconButtonProps): ReactElement => {
  const ttId = tooltipId ?? tooltip?.replace(/ /g, '-').toLowerCase();

  return (
    <button
      type={isSubmit ? 'submit' : 'button'}
      aria-label={ariaLabel ?? tooltip}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      data-active={active}
      data-tooltip-place="bottom"
      {...(ttId ? { 'data-tooltip-id': ttId } : {})}
      {...(tooltip ? { 'data-tooltip-content': tooltip } : {})}
      data-tooltip-delay-show={350}
      onClick={onClick}
      disabled={disabled}
      id={id}
      {...getIconButtonStyles({
        type,
        size,
        disabled: !!disabled,
        additionalClassnames: className,
      })}
    >
      <IconButtonIcon
        Icon={Icon}
        iconClassName={iconClassName ?? ''}
        type={type}
        size={size}
      />
      {children}
      {ttId && (
        <Tooltip
          openEvents={{ mouseover: true }}
          closeEvents={{ click: true, mouseleave: true, blur: true }}
          className="z-20"
          id={ttId}
        />
      )}
    </button>
  );
};

